import { useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import type { ClientRecord, ClientAuth } from '@/api/clients';
import { useAuthStore } from '@/stores/auth';
import { fmtDob } from '../clientHelpers';
import { AuthDialog } from '../AuthDialog';
import { cn } from '@/lib/cn';

/*
 * Authorizations tab — read view + launchers for the add/edit dialog.
 * Each auth carries a server-assigned UUID so edits/deletes reference
 * the entry id, never an array index. Add/edit/delete all hit dedicated
 * endpoints that write an audit row server-side.
 */

function authStatus(a: ClientAuth): string {
    if (!a.end) return 'Pending';
    const now  = Date.now();
    const soon = now + 30 * 86400000;
    const end  = Date.parse(`${a.end}T00:00:00`);
    if (Number.isNaN(end)) return 'Pending';
    if (end < now)  return 'Expired';
    if (end < soon) return 'Expiring';
    if (a.start) {
        const start = Date.parse(`${a.start}T00:00:00`);
        if (!Number.isNaN(start) && start > now) return 'Pending';
    }
    return 'Active';
}

export function ClientAuthTab({ client: c }: { client: ClientRecord }) {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_clients'));
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAuth, setEditingAuth] = useState<ClientAuth | null>(null);

    const auths = c.auths || [];

    function openAdd() {
        setEditingAuth(null);
        setDialogOpen(true);
    }

    function openEdit(auth: ClientAuth) {
        setEditingAuth(auth);
        setDialogOpen(true);
    }

    const header = (
        <CardHeader className="px-5 pt-5 flex items-start justify-between">
            <CardTitle>Authorizations</CardTitle>
            {canEdit && (
                <Button type="button" size="sm" variant="primary" onClick={openAdd}>
                    <Plus className="w-3.5 h-3.5" /> Add Authorization
                </Button>
            )}
        </CardHeader>
    );

    return (
        <>
            <Card className="p-0 overflow-hidden">
                {header}
                {auths.length === 0 ? (
                    <p className="px-5 pb-5 text-sm text-text-subtle">No authorizations on file.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-5 py-2 font-bold">Payer</th>
                                    <th className="px-3 py-2 font-bold">Auth #</th>
                                    <th className="px-3 py-2 font-bold">Code</th>
                                    <th className="px-3 py-2 font-bold text-right">Hours</th>
                                    <th className="px-3 py-2 font-bold text-right">Used</th>
                                    <th className="px-3 py-2 font-bold text-right">Remaining</th>
                                    <th className="px-3 py-2 font-bold">Start</th>
                                    <th className="px-3 py-2 font-bold">End</th>
                                    <th className="px-5 py-2 font-bold">Status</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-12"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {auths.map((a) => {
                                    const hours = Number(a.hours) || 0;
                                    const used  = Number(a.used)  || 0;
                                    const rem   = Math.max(0, hours - used);
                                    const pct   = hours ? Math.min(100, Math.round((used / hours) * 100)) : 0;
                                    const fill  = pct >= 80 ? 'bg-error' : pct >= 50 ? 'bg-warning' : 'bg-accent-dark';
                                    return (
                                        <tr key={a.id} className="border-b border-border last:border-b-0">
                                            <td className="px-5 py-2 text-text">{a.payer || '—'}</td>
                                            <td className="px-3 py-2 text-text-muted font-mono text-xs">{a.number || '—'}</td>
                                            <td className="px-3 py-2 text-text-muted">{a.code || '—'}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{hours}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                <div className="flex items-center justify-end gap-2">
                                                    <span>{used}</span>
                                                    <div className="h-1 w-14 rounded-full bg-surface-muted overflow-hidden">
                                                        <div className={cn('h-full', fill)} style={{ width: `${pct}%` }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">{rem}</td>
                                            <td className="px-3 py-2 text-text-muted whitespace-nowrap">{a.start ? fmtDob(a.start) : '—'}</td>
                                            <td className="px-3 py-2 text-text-muted whitespace-nowrap">{a.end   ? fmtDob(a.end)   : '—'}</td>
                                            <td className="px-5 py-2"><StatusBadge status={authStatus(a)} /></td>
                                            {canEdit && (
                                                <td className="px-3 py-2 text-right">
                                                    <button
                                                        type="button"
                                                        aria-label="Edit authorization"
                                                        title="Edit authorization"
                                                        onClick={() => openEdit(a)}
                                                        className="p-1 rounded-sm text-text-muted hover:text-text hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {canEdit && (
                <AuthDialog
                    clientId={c.id}
                    auth={editingAuth}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                />
            )}
        </>
    );
}
